import { CssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App.js";
import { AuthProvider } from "./auth/AuthProvider.js";
import { Toaster } from "./components/ui/toaster.js";
import { installPromiseWithResolvers } from "./lib/promiseWithResolvers.js";
import { ThemeModeProvider } from "./styles/ThemeModeProvider.js";
import "./styles/globals.css";

// pdf.js needs `Promise.withResolvers` (Chrome 119+ / Safari 17.4+ / Firefox
// 121+). Install it BEFORE anything imports the PDF reader, or an invoice,
// quote or documentation preview crashes on an older browser — and a partner
// portal does not get to choose which browsers its resellers use.
installPromiseWithResolvers();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      retry: 1,
    },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root element not found");

createRoot(rootEl).render(
  <StrictMode>
    <ThemeModeProvider>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
        {/* Mounted ONCE, above the router, so every screen can raise a toast
            — the CRM, but also login and onboarding, which sit outside the
            signed-in shell. A second <Toaster/> anywhere would draw every
            toast twice (they all read one module-level store), so add call
            sites, never another container. */}
        <Toaster />
      </QueryClientProvider>
    </ThemeModeProvider>
  </StrictMode>,
);
